import { z } from 'zod';
import { ECardPurpose } from '~/enums';

const cardOfferGetOfferResDTOSignerSchema = z.strictObject({
  signerId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  personalNumber: z.string().nullish(),
  dateOfBirth: z.string(),
});

const cardOfferGetOfferResDTOBeneficialOwnerSchema = z.strictObject({
  fullName: z.string(),
  personalNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  ownership: z
    .string()
    .refine((ownership) => {
      return Number(ownership);
    }, 'ownership must be a valid number')
    .optional(),
  residenceCountry: z.string(),
});

const cardOfferGetOfferResDTOSchema = z.strictObject({
  applicationUuid: z.string(),
  offeredCreditLimit: z.number(),
  clientId: z.number(),
  additionalSigners: z.array(cardOfferGetOfferResDTOSignerSchema),
  beneficialOwners: z.array(cardOfferGetOfferResDTOBeneficialOwnerSchema),
  applicationReasons: z.array(
    z.strictObject({
      reason: z.nativeEnum(ECardPurpose),
      reasonText: z.string().nullable(),
    })
  ),
  applicant: z.strictObject({
    signerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
    personalNumber: z.string().nullish(),
    dateOfBirth: z.string().nullable(),
    signerUuid: z.string(),
  }),
  companyName: z.string(),
  companyType: z.string(),
});

const cardOfferPostOfferReqDTOBeneficialOwnerSchema = z.strictObject({
  fullName: z.string(),
  id: z.string(),
  isAlternativeOwner: z.boolean(),
  personalNumber: z.string().optional(),
  dateOfBirth: z.string().optional(),
  address: z.object({
    zipCode: z.string().nullable(),
    street: z.string().nullable(),
    city: z.string().nullable(),
  }),
  ownerShare: z.string(), // is added because expected by svc-card-java
  ownership: z.string(), // is used in the Roaring API comparison
  residenceCountryIsoCode: z.string().min(2).max(2),
  taxResidenceCountryIsoCode: z.string().min(2).max(2),
  residenceCountry: z.string(), // is used in the Roaring API comparison
  politicallyExposed: z.boolean(),
});

const cardOfferPostOfferReqDTOApplicantSchema = z.strictObject({
  signerId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneNumber: z.string(),
  residenceCountryIsoCode: z.string().min(2).max(2),
  taxResidenceCountryIsoCode: z.string().min(2).max(2),
  politicallyExposed: z.boolean(),
  personalNumber: z.string(),
  dateOfBirth: z.string().nullable(),
  fullName: z.string(),
  signerUuid: z.string(),
  ownership: z.string(),
  id: z.string(),
});

const cardOfferPostOfferReqDTOSignerSchema = z.strictObject({
  name: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  signerId: z.number(),
  email: z.string(),
  phoneNumber: z.string(),
  type: z.string(),
  residenceCountryIsoCode: z.string().min(2).max(2),
  taxResidenceCountryIsoCode: z.string().min(2).max(2),
  politicallyExposed: z.boolean(),
});

const cardOfferPostOfferReqDTOSchema = z.strictObject({
  applicant: cardOfferPostOfferReqDTOApplicantSchema,
  additionalSigners: z.array(cardOfferPostOfferReqDTOSignerSchema),
  beneficialOwners: z.array(cardOfferPostOfferReqDTOBeneficialOwnerSchema),
  hasAcceptedTerms: z.boolean(),
  applicationReasons: z.array(
    z.strictObject({
      reason: z.nativeEnum(ECardPurpose),
      reasonText: z.string().nullable(),
    })
  ),
});

const cardOfferPostOfferResDTOSchema = z.strictObject({
  signatureUrl: z.string(),
  hash: z.string().optional(),
  message: z.string().optional(), // this is only sent back in case of an error
});

export const cardOfferDTOSchemas = {
  getOfferRes: cardOfferGetOfferResDTOSchema,
  postOfferReqBeneficialOwner: cardOfferPostOfferReqDTOBeneficialOwnerSchema,
  postOfferReqSigner: cardOfferPostOfferReqDTOSignerSchema,
  postOfferReq: cardOfferPostOfferReqDTOSchema,
  postOfferRes: cardOfferPostOfferResDTOSchema,
};
